import cx from "classnames";
import type { FC } from "react";
import { Children } from "react";

import { Avatar, AvatarSize, AvatarType } from "../Avatar";

export type AvatarGroupProps = {
	max?: number;
	avatarSize?: AvatarSize;
};

type AvatarFCProps = AvatarGroupProps & React.HTMLAttributes<HTMLElement>;

/* Figure out how many avatars to show if max is given. */
const getVisible = (length: number, max?: number): number => {
	if (max === undefined) {
		return length;
	}

	return Math.min(length, max);
};

export const AvatarGroup: FC<AvatarFCProps> = ({
	max,
	children,
	avatarSize = AvatarSize.md,
	...props
}) => {
	const childArr = Children.toArray(children);
	const visible = getVisible(childArr.length, max);
	const remainder = childArr.length - visible;
	const remainderText = remainder > 99 ? "..." : `+${remainder}`;

	const Remainder = () => {
		return (
			<Avatar
				userName={remainderText}
				avatarType={AvatarType.Text}
				label={remainderText}
				size={avatarSize}
				isOverflow
			/>
		);
	};

	const childElms = childArr.slice(0, visible);

	if (remainder > 0) {
		childElms.push(Remainder());
	}

	const avatarClasses = [
		avatarSize === AvatarSize.xxs && ["[&>*]:border-2", "[&:not(:first-child)]:-ml-1"],
		avatarSize === AvatarSize.xs && ["[&>*]:border-3", "[&:not(:first-child)]:-ml-2"],
		avatarSize === AvatarSize.sm && ["[&>*]:border-3", "[&:not(:first-child)]:-ml-3"],
		avatarSize === AvatarSize.md && ["[&>*]:border-3", "[&:not(:first-child)]:-ml-3"],
		avatarSize === AvatarSize.lg && ["[&>*]:border-4", "[&:not(:first-child)]:-ml-4"],
		avatarSize === AvatarSize.xl && ["[&>*]:border-4", "[&:not(:first-child)]:-ml-4"],
	];

	return (
		<div className={cx("inline-flex")} {...props}>
			{childElms.map((child, index) => {
				if (child === null) {
					return null;
				}

				return (
					<div
						className={cx(
							"[&>*]:rounded-full",
							"[&>*]:block",
							"grow-0",
							"shrink-0",
							"transition-none",
							"[&>*]:border-bg-primary",
							"group-hover:border-bg-secondary",
							"group-focus:border-bg-tertiary",
							"group-active:border-bg-quaternary",
							avatarClasses
						)}
						key={"childElm-" + index}
					>
						{child}
					</div>
				);
			})}
		</div>
	);
};
